import { useContext, useMemo } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { isSymbol } from "@shared/types/utils";
import { BotData, BotOptions, ClientData, NumberOption } from "@shared/types";
import { intersection } from "lodash";
import {
  AccountOverlapData,
  BotFormType,
  OverlapData,
} from "@shared/features/bots/BotForm/common/types";
import { useMarginOptions } from "@shared/features/bots/BotForm/common/hooks";

interface useBulkActionAccountsOverlapDataProps {
  botsData?: BotData[] | undefined;
}

const useBulkActionAccountsOverlapData = ({
  botsData,
}: useBulkActionAccountsOverlapDataProps) => {
  const {
    accounts: formAccounts,
    symbols,
    useSymbolFromAlert,
    exchanges,
    getBulkBotCreateGeneralDataStatus,
    botFormType,
    symbolsMetadata,
  } = useContext(BotFormContext)!;
  const { data: botsBulkGeneralData } = getBulkBotCreateGeneralDataStatus;
  const isBulkCreate = botFormType === BotFormType.BULK_CREATE;
  const { getMarginOptions } = useMarginOptions();

  const processOverlapData = <
    Option extends NumberOption | string = NumberOption,
    Value extends string | number = number
  >(
    overlapKey: keyof OverlapData,
    overlapValues: OverlapData,
    options?: Option[],
    getOptionValue?: (option: Option) => Value
  ) => {
    const overlapData = overlapValues[
      overlapKey
    ] as unknown as AccountOverlapData<Value, Option>;
    const values =
      (getOptionValue
        ? options?.map((option) => getOptionValue(option))
        : options) || [];
    if (!overlapData) {
      overlapValues[overlapKey] = {
        overlappingValues: values as any,
        optionsValues: new Set(values as any) as any,
        allOptions: [...(options || [])] as any,
      };
    } else {
      overlapData.overlappingValues = intersection(
        [...overlapData.overlappingValues],
        values as any
      );
      options?.forEach((option) => {
        const optionValue = (
          getOptionValue ? getOptionValue(option) : option
        ) as any;
        const shouldAdd = !overlapData.optionsValues.has(optionValue);
        if (shouldAdd) {
          overlapData.optionsValues.add(optionValue);
          overlapData.allOptions.push(option);
        }
      });
    }
  };

  const processNumberOptionOverlapData = (
    overlapKey: keyof OverlapData,
    overlapValues: OverlapData,
    options?: NumberOption[]
  ) => {
    return processOverlapData(
      overlapKey,
      overlapValues,
      options,
      (option) => option.value
    );
  };

  const getDefaultOverlappingValues = (): OverlapData => {
    return {
      longShort: undefined,
      marginType: undefined,
      multiplier: undefined,
      symbols: undefined,
      stopType: undefined,
    };
  };

  const processBotOverlapData = (
    overlapValues: OverlapData,
    botOptions?: BotOptions,
    account?: ClientData
  ) => {
    if (!botOptions) return;
    const { marginTypeOptions, multiplierOptions, longShortOptions } =
      getMarginOptions(
        botOptions,
        useSymbolFromAlert ? undefined : symbols,
        exchanges,
        account?.exchange
      );
    if (isBulkCreate) {
      processNumberOptionOverlapData(
        "longShort",
        overlapValues,
        longShortOptions
      );
      processNumberOptionOverlapData(
        "marginType",
        overlapValues,
        marginTypeOptions
      );
      processNumberOptionOverlapData(
        "multiplier",
        overlapValues,
        multiplierOptions
      );
      if (account) {
        processOverlapData(
          "symbols",
          overlapValues,
          symbolsMetadata?.getAccountActiveSymbols(account.client_id)
        );
      }
    }
    processNumberOptionOverlapData(
      "stopType",
      overlapValues,
      botOptions?.["Stop Type"]?.[0]
    );
  };

  const accountsOverlapData = useMemo(() => {
    const overlapValues = getDefaultOverlappingValues();
    if (isBulkCreate) {
      if (isSymbol(formAccounts) || !formAccounts || !botsBulkGeneralData) {
        return overlapValues;
      } else {
        formAccounts.forEach((account) => {
          processBotOverlapData(
            overlapValues,
            botsBulkGeneralData[account.client_id]?.options,
            account
          );
        });
      }
    } else if (botsData) {
      botsData.forEach((botData) => {
        processBotOverlapData(overlapValues, botData.options);
      });
    }
    return overlapValues;
  }, [formAccounts, botsBulkGeneralData, symbols]);

  return {
    accountsOverlapData,
  };
};

export default useBulkActionAccountsOverlapData;
